import { useContext } from "react";
import { HomeContext } from "../../../containers/Home";
import { useMyTasksSummary } from "../../../hooks/useDashboardQueries";
import {
	MyTasksSummary,
	TaskBucket,
	Urgency,
	UrgencyCounts,
} from "../../../types/dashboard.types";
import { cardStatus } from "../cardStatus";
import StackedBarChart, { StackedBarDatum } from "../charts/StackedBarChart";
import DashboardCard from "../DashboardCard";
import {
	BADGES,
	formatCount,
	TASK_BUCKETS,
	URGENCY_SERIES,
} from "../dashboardConfig";
import ExpandedWidgetModal from "../ExpandedWidgetModal";
import { MultiSelectField, TenantField } from "../FilterControls";
import { TenantChoice, TenantWidgetProps } from "../tenantChoice";
import {
	useDraftFilters,
	useExpandedView,
	useGuardedNavigate,
	useHasOpened,
	useTenantChoice,
} from "../widgetHooks";
import WidgetSelect from "../WidgetSelect";
import WidgetState from "../WidgetState";

const TITLE = "My tasks";
const EMPTY_TEXT = "No pending tasks";
const MY_TASKS_PATH = "/my-tasks";
const NO_TASKS: UrgencyCounts = { overdue: 0, attention: 0, onTrack: 0 };
const TYPE_OPTIONS = TASK_BUCKETS.map(({ key, label }) => ({
	value: key,
	label,
}));
const DUE_OPTIONS = URGENCY_SERIES.map(({ key, label }) => ({
	value: key,
	label,
}));
const urgencyColor = (key: Urgency) =>
	URGENCY_SERIES.find((series) => series.key === key)?.color;

interface TaskFilters {
	types?: TaskBucket[];
	due?: Urgency[];
}

/** One bar per task type; optional types only when they have tasks. */
const toBars = (
	data: MyTasksSummary,
	{ types = [], due = [] }: TaskFilters = {}
): StackedBarDatum[] =>
	TASK_BUCKETS.map((bucket) => ({
		bucket,
		counts: data.byType?.[bucket.key] ?? NO_TASKS,
	}))
		.filter(({ bucket, counts }) =>
			types.length
				? types.includes(bucket.key)
				: !bucket.optional ||
					counts.overdue + counts.attention + counts.onTrack > 0
		)
		.map(({ bucket, counts }) => ({
			id: bucket.key,
			label: bucket.label,
			fullLabel: bucket.label,
			overdue: !due.length || due.includes("overdue") ? counts.overdue : 0,
			attention:
				!due.length || due.includes("attention") ? counts.attention : 0,
			onTrack: !due.length || due.includes("onTrack") ? counts.onTrack : 0,
		}));

const hasNoTasks = (data: MyTasksSummary, filters?: TaskFilters) =>
	toBars(data, filters).every(
		(bar) =>
			!Number(bar.overdue) && !Number(bar.attention) && !Number(bar.onTrack)
	);

interface MyTasksChartProps {
	data: MyTasksSummary;
	filters?: TaskFilters;
	context: string;
}

function MyTasksChart({ data, filters = {}, context }: MyTasksChartProps) {
	const bars = toBars(data, filters);
	const series = filters.due?.length
		? URGENCY_SERIES.filter((item) => filters.due?.includes(item.key))
		: URGENCY_SERIES;
	return (
		<div className="dashboard-tasks__chart">
			<h3 className="dashboard-tasks__chart-title">Pending tasks by type</h3>
			<StackedBarChart
				data={bars}
				series={series}
				summary={`Pending tasks by type, ${context}: ${bars
					.map(
						(bar) =>
							`${bar.label} ${bar.overdue} overdue, ${bar.attention} needing attention, ${bar.onTrack} on track`
					)
					.join("; ")}.`}
			/>
		</div>
	);
}

interface ExpandedProps {
	open: boolean;
	onClose: () => void;
	choice: TenantChoice;
	initialTenant: string;
	email: string | undefined;
}

function MyTasksExpanded({
	open,
	onClose,
	choice,
	initialTenant,
	email,
}: ExpandedProps) {
	const hasOpened = useHasOpened(open);
	const filters = useDraftFilters<{
		tenant: string;
		types: TaskBucket[];
		due: Urgency[];
	}>({ tenant: initialTenant, types: [], due: [] });
	const { applied, draft } = filters;
	const { target, label } = choice.resolve(applied.tenant);
	const query = useMyTasksSummary(hasOpened ? target : null, email);

	return (
		<ExpandedWidgetModal
			open={open}
			onClose={onClose}
			badge={BADGES.tasks}
			title={TITLE}
			context={label}
			onApply={filters.apply}
			onReset={filters.reset}
			filters={
				<>
					<TenantField
						value={draft.tenant}
						options={choice.options}
						onChange={(tenant) => filters.update({ tenant })}
					/>
					<MultiSelectField
						label="Task type"
						options={TYPE_OPTIONS}
						values={draft.types}
						onChange={(types) =>
							filters.update({ types: types as TaskBucket[] })
						}
					/>
					<MultiSelectField
						label="Due"
						options={DUE_OPTIONS}
						values={draft.due}
						onChange={(due) => filters.update({ due: due as Urgency[] })}
					/>
				</>
			}
		>
			<WidgetState
				status={cardStatus(query, (data) => hasNoTasks(data, applied))}
				emptyText={EMPTY_TEXT}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<MyTasksChart data={query.data} filters={applied} context={label} />
				)}
			</WidgetState>
		</ExpandedWidgetModal>
	);
}

/** The caller's pending tasks, as the My Tasks page lists them, by type and urgency. */
function MyTasksWidget({ tenants, currentTenantKey }: TenantWidgetProps) {
	const { userInfo }: any = useContext(HomeContext);
	const email: string | undefined = userInfo?.user?.email;
	const { onLinkClick } = useGuardedNavigate();
	const { choice, tenant, setTenant, target, label } = useTenantChoice(
		tenants,
		"mine",
		currentTenantKey
	);
	const expanded = useExpandedView();
	const query = useMyTasksSummary(target, email);
	const totals = query.data?.totals;

	return (
		<>
			<DashboardCard
				badge={BADGES.tasks}
				title={TITLE}
				className="dashboard-widget--my-tasks dashboard-widget--short"
				controls={
					<WidgetSelect
						value={tenant}
						options={choice.options}
						onChange={setTenant}
						ariaLabel={`Tenant for ${TITLE}`}
					/>
				}
				onExpand={expanded.show}
				status={cardStatus(query, (data) => data.totals.pending === 0)}
				emptyText={EMPTY_TEXT}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && totals && (
					<div className="dashboard-tasks">
						<a
							href={MY_TASKS_PATH}
							className="dashboard-tasks__summary"
							onClick={onLinkClick(MY_TASKS_PATH)}
						>
							<span>My tasks</span>
							<span>
								<span className="dashboard-tasks__pending">
									{formatCount(totals.pending, query.data.truncated)}
								</span>{" "}
								pending
							</span>
							<span className="dashboard-tasks__divider" />
							<span className="dashboard-tasks__due">
								<span
									className="dashboard-tasks__dot"
									style={{ background: urgencyColor("overdue") }}
								/>
								{formatCount(totals.overdue)} overdue
							</span>
							<span className="dashboard-tasks__due">
								<span
									className="dashboard-tasks__dot"
									style={{ background: urgencyColor("attention") }}
								/>
								{formatCount(totals.attention)} need attention
							</span>
							<span className="dashboard-tasks__open">
								Open My Tasks <span aria-hidden="true">→</span>
							</span>
						</a>
						<MyTasksChart data={query.data} context={label} />
					</div>
				)}
			</DashboardCard>
			<MyTasksExpanded
				key={expanded.session}
				open={expanded.open}
				onClose={expanded.hide}
				choice={choice}
				initialTenant={tenant}
				email={email}
			/>
		</>
	);
}

export default MyTasksWidget;
